// Service catalogue — mirrors Alpha Community Care's real service offerings.
import {
  Home, HeartPulse, Users, Stethoscope, ShieldAlert, HandHeart, Accessibility,
  Clock, CalendarClock, Sparkles,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "daily-living",
    short: "Daily Living",
    title: "Assistance with Daily Living",
    Icon: Sparkles,
    intro:
      "Practical, person-centred support to help you manage the everyday — at home, in your own way.",
    forWho: [
      "Participants who need a hand with personal care, cooking, cleaning or routines",
      "Families looking for reliable, consistent daily support",
      "Participants building confidence and independence at home",
    ],
    delivery: [
      "Matched support workers based on values, preferences and cultural fit",
      "Regular rostering so you see familiar faces",
      "Flexible hours, weekdays, evenings and weekends",
    ],
    inclusions: [
      "Personal care and hygiene support",
      "Meal planning and preparation",
      "Household tasks, shopping and errands",
      "Routine building and life-skills coaching",
    ],
    related: ["in-home-support", "community-participation", "sil"],
  },
  {
    slug: "sil",
    short: "Supported Independent Living (SIL)",
    title: "Supported Independent Living",
    Icon: Home,
    intro:
      "24/7 in-home support in a household that feels like home — with the right team around you.",
    forWho: [
      "Participants needing overnight and active support",
      "Participants leaving hospital, family home or another provider",
      "People with complex, high-intensity or forensic overlay needs",
    ],
    delivery: [
      "Collaborative house culture with consistent rostering",
      "Clinical oversight and individualised support plans",
      "Active overnight and sleepover options",
    ],
    inclusions: [
      "24/7 staffing and support",
      "Behavioural, clinical and allied-health coordination",
      "Goal-aligned daily routines",
      "Household management and shared living skills",
    ],
    related: ["sda", "daily-living", "community-nursing"],
  },
  {
    slug: "support-coordination",
    short: "Support Coordination",
    title: "Support Coordination",
    Icon: Users,
    intro:
      "A steady partner in your corner — helping you understand, activate and get the best from your NDIS plan.",
    forWho: [
      "Participants new to the NDIS or changing providers",
      "Families navigating complex plan budgets",
      "Participants with coordination supports in their plan",
    ],
    delivery: [
      "A named coordinator who knows your plan and your goals",
      "Warm referrals to vetted providers",
      "Clear, plain-English communication with you and your circle",
    ],
    inclusions: [
      "Plan activation and budget mapping",
      "Provider matching and onboarding",
      "Crisis response and escalation",
      "Reporting and plan review preparation",
    ],
    related: ["in-home-support", "daily-living", "community-participation"],
  },
  {
    slug: "community-participation",
    short: "Community Participation",
    title: "Community Participation",
    Icon: HandHeart,
    intro:
      "Real social connection, skills and community life — tailored to what you genuinely enjoy.",
    forWho: [
      "Participants wanting to re-engage with community life",
      "People building friendships and routines outside the home",
      "Participants working on confidence or mobility in public settings",
    ],
    delivery: [
      "1:1 and small-group supports",
      "Culturally-responsive and LGBTQIA+ friendly options",
      "Consistent workers who know your interests",
    ],
    inclusions: [
      "Social and recreational outings",
      "Community access and transport support",
      "Skill-building in public settings",
      "Interest-based programs",
    ],
    related: ["daily-living", "in-home-support", "sil"],
  },
  {
    slug: "community-nursing",
    short: "Community Nursing Care",
    title: "Community Nursing Care",
    Icon: Stethoscope,
    intro:
      "Clinically-led nursing support delivered in your home, so complex health needs are met with dignity.",
    forWho: [
      "Participants with complex health or wound-care needs",
      "People with enteral nutrition, continence or medication support needs",
      "Participants transitioning from hospital",
    ],
    delivery: [
      "Registered and enrolled nurses under clinical governance",
      "Coordinated care with GPs, specialists and allied health",
      "Consistent clinicians, continuity of care",
    ],
    inclusions: [
      "Medication management and administration",
      "Wound and pressure-area care",
      "Continence assessment and management",
      "Enteral feeding and tracheostomy support",
    ],
    related: ["sil", "sda", "daily-living"],
  },
  {
    slug: "forensic-disability-services",
    short: "Forensic Disability Services",
    title: "Forensic Disability Services",
    Icon: ShieldAlert,
    intro:
      "Specialist, restorative support for participants engaged with the justice system — delivered with care, structure and respect.",
    forWho: [
      "Participants with forensic overlays in their NDIS plan",
      "People on community-based or custodial orders",
      "Participants requiring structured, trauma-informed support",
    ],
    delivery: [
      "Trauma-informed, restorative approach",
      "Collaboration with legal, clinical and justice stakeholders",
      "Specialist staff training and supervision",
    ],
    inclusions: [
      "Forensic-overlay SIL support",
      "Behavioural support coordination",
      "Risk, safety and positive-behaviour planning",
      "Justice liaison and transition planning",
    ],
    related: ["sil", "sda", "support-coordination"],
  },
  {
    slug: "in-home-support",
    short: "In-Home Support",
    title: "In-Home Support",
    Icon: HeartPulse,
    intro:
      "Flexible one-on-one support in your own home — matched to how you live and what you need.",
    forWho: [
      "Participants living at home with family",
      "People who need a few hours a week of reliable support",
      "Participants recovering from hospital stays",
    ],
    delivery: [
      "Consistent workers, familiar faces",
      "Flexible shift lengths",
      "Respite options for primary carers",
    ],
    inclusions: [
      "Personal care at home",
      "Domestic assistance",
      "Transport and appointments",
      "Respite and overnight support",
    ],
    related: ["daily-living", "community-participation", "support-coordination"],
  },
  {
    slug: "sda",
    short: "Specialist Disability Accommodation (SDA)",
    title: "Specialist Disability Accommodation",
    Icon: Accessibility,
    intro:
      "Purpose-built, high-quality homes for participants with extreme functional impairment or very high support needs.",
    forWho: [
      "Participants with SDA funding in their plan",
      "People needing Fully Accessible, High Physical Support, Improved Liveability or Robust homes",
      "Participants leaving aged care or hospital",
    ],
    delivery: [
      "Carefully designed homes across Melbourne and Perth",
      "Co-located 24/7 SIL where funded",
      "Warm, considered interiors — not institutional",
    ],
    inclusions: [
      "All four SDA design categories",
      "On-site support options",
      "Accessibility features built in",
      "Tenancy and transition support",
    ],
    related: ["sil", "mta", "sta"],
  },
  {
    slug: "sta",
    short: "Short Term Accommodation (STA)",
    title: "Short Term Accommodation",
    Icon: Clock,
    intro:
      "Supported respite and short stays — for planned breaks, emergencies, or trying something new.",
    forWho: [
      "Participants needing planned respite",
      "Families needing short-term support",
      "Participants trialling a new living arrangement",
    ],
    delivery: [
      "1 to 14 night stays",
      "Consistent, trained support workers",
      "Welcoming, home-like environments",
    ],
    inclusions: [
      "Accommodation and meals",
      "24/7 support",
      "Activities and community access",
      "Coordination with existing supports",
    ],
    related: ["mta", "sil", "in-home-support"],
  },
  {
    slug: "mta",
    short: "Medium Term Accommodation (MTA)",
    title: "Medium Term Accommodation",
    Icon: CalendarClock,
    intro:
      "Bridging housing while your long-term home is arranged — so you're not stuck between options.",
    forWho: [
      "Participants waiting for an SDA or SIL placement",
      "People discharging from hospital or rehabilitation",
      "Participants between tenancies",
    ],
    delivery: [
      "Stays up to 90 days (and beyond where funded)",
      "Fully supported",
      "Warm handover into long-term housing",
    ],
    inclusions: [
      "Accommodation and utilities",
      "24/7 support options",
      "Coordination with next placement",
      "Goal planning for transition",
    ],
    related: ["sil", "sda", "sta"],
  },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

export const COMMON_FAQS = [
  {
    q: "Are you a registered NDIS provider?",
    a: "Yes. Alpha Community Care is a registered NDIS provider, operating under the NDIS Practice Standards with qualified, verified staff and active clinical governance.",
  },
  {
    q: "Where do you deliver services?",
    a: "We currently deliver services across Victoria (with our head office in Docklands, Melbourne) and Western Australia (with operations in West Perth and surrounding suburbs).",
  },
  {
    q: "How do I start a service or make a referral?",
    a: "Call 1300 380 692, email hello@alphacommunitycare.com.au, or complete our referral form. We respond within one business day and will guide you through intake.",
  },
  {
    q: "Do you support complex, high-intensity or forensic needs?",
    a: "Yes. We run 24/7 SIL homes with clinical oversight, including forensic-overlay support delivered by specifically-trained staff.",
  },
  {
    q: "Can my support coordinator refer someone on my behalf?",
    a: "Absolutely. We work closely with support coordinators, case managers and allied health teams. Use our coordinator referral form for fast, compliant intake.",
  },
];
